import React, { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material'
import { Settings as SettingsIcon } from '@mui/icons-material'
import {
  SystemConfigEntry,
  useDeactivateSystemConfigMutation,
  useGetSystemConfigQuery,
  useUpsertSystemConfigMutation,
} from '@store/api/adminApi'
import { useAdminPermissions } from '@hooks/useAdminPermissions'
import { useGetSiteFlagsQuery } from '@store/api/siteApi'
import AdminPageGate from '@components/auth/AdminPageGate'

const CATEGORIES = ['general', 'notifications', 'features', 'limits', 'security'] as const

const PLATFORM_PRESETS: Array<{
  key: string
  category: string
  description: string
  value: Record<string, unknown>
}> = [
  {
    key: 'notifications.announcement',
    category: 'notifications',
    description: 'Platform-wide info banner shown to signed-in users',
    value: { active: false, title: '', message: '' },
  },
  {
    key: 'notifications.maintenance',
    category: 'notifications',
    description: 'Maintenance warning banner (higher visibility)',
    value: { active: false, title: 'Scheduled maintenance', message: '' },
  },
]

const formatValue = (value: unknown): string => JSON.stringify(value, null, 2)

const SystemSettings: React.FC = () => {
  const [includeInactive, setIncludeInactive] = useState(false)
  const [categoryFilter, setCategoryFilter] = useState('')
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [deactivateTarget, setDeactivateTarget] = useState<string | null>(null)
  const { can, isReady } = useAdminPermissions()
  const [editEntry, setEditEntry] = useState<SystemConfigEntry | null>(null)
  const [editKey, setEditKey] = useState('')
  const [editCategory, setEditCategory] = useState('general')
  const [editDescription, setEditDescription] = useState('')
  const [editValueJson, setEditValueJson] = useState('{}')
  const [editActive, setEditActive] = useState(true)
  const [betaSaving, setBetaSaving] = useState(false)

  const { data, isLoading, error } = useGetSystemConfigQuery({
    category: categoryFilter || undefined,
    includeInactive,
  })
  const { data: siteFlags, refetch: refetchSiteFlags } = useGetSiteFlagsQuery()
  const [upsert, { isLoading: saving }] = useUpsertSystemConfigMutation()
  const [deactivate, { isLoading: deactivating }] = useDeactivateSystemConfigMutation()

  const grouped = useMemo(() => {
    const configs = data?.data ?? []
    const map = new Map<string, SystemConfigEntry[]>()
    for (const row of configs) {
      const cat = row.category || 'general'
      if (!map.has(cat)) map.set(cat, [])
      map.get(cat)!.push(row)
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b))
  }, [data?.data])

  const openCreate = (preset?: (typeof PLATFORM_PRESETS)[number]) => {
    setEditEntry(null)
    setEditKey(preset?.key ?? '')
    setEditCategory(preset?.category ?? 'general')
    setEditDescription(preset?.description ?? '')
    setEditValueJson(formatValue(preset?.value ?? {}))
    setEditActive(true)
    setDialogOpen(true)
  }

  const openEdit = (row: SystemConfigEntry) => {
    setEditEntry(row)
    setEditKey(row.key)
    setEditCategory(row.category)
    setEditDescription(row.description ?? '')
    setEditValueJson(formatValue(row.value))
    setEditActive(row.isActive)
    setDialogOpen(true)
  }

  const closeDialog = () => {
    setDialogOpen(false)
    setEditEntry(null)
    setEditKey('')
  }

  const handleSave = async () => {
    let parsed: unknown
    try {
      parsed = JSON.parse(editValueJson)
    } catch {
      setMessage({ type: 'error', text: 'Value must be valid JSON' })
      return
    }
    try {
      await upsert({
        key: editKey.trim(),
        value: parsed,
        category: editCategory,
        description: editDescription || undefined,
        isActive: editActive,
      }).unwrap()
      setMessage({ type: 'success', text: `Saved ${editKey}` })
      closeDialog()
    } catch (err: unknown) {
      const apiErr = err as { data?: { error?: { message?: string } } }
      setMessage({
        type: 'error',
        text: apiErr?.data?.error?.message ?? 'Failed to save configuration',
      })
    }
  }

  const confirmDeactivate = async () => {
    if (!deactivateTarget) return
    try {
      await deactivate(deactivateTarget).unwrap()
      setMessage({ type: 'success', text: `Deactivated ${deactivateTarget}` })
      setDeactivateTarget(null)
    } catch {
      setMessage({ type: 'error', text: 'Failed to deactivate configuration' })
    }
  }

  const betaEnabled = siteFlags?.beta !== false

  const handleBetaToggle = async (enabled: boolean) => {
    setBetaSaving(true)
    try {
      await upsert({
        key: 'features.beta',
        category: 'features',
        description: 'When enabled, the site shows only the free plan and hides paid tiers and upgrades.',
        value: { enabled },
        isActive: true,
      }).unwrap()
      await refetchSiteFlags()
      setMessage({
        type: 'success',
        text: enabled
          ? 'Beta is on. Paid plans and upgrades are hidden.'
          : 'Beta is off. Paid tiers and upgrade options are visible.',
      })
    } catch {
      setMessage({ type: 'error', text: 'Failed to update beta flag' })
    } finally {
      setBetaSaving(false)
    }
  }

  if (isReady && !can('system:config')) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          System Settings
        </Typography>
        <Alert severity="warning">
          Your role does not include the <strong>system:config</strong> permission.
        </Alert>
      </Box>
    )
  }

  return (
    <AdminPageGate permission="system:config" title="System Settings">
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <SettingsIcon color="primary" />
        <Typography variant="h4" component="h1">
          System Settings
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Key/value configuration stored in the database. Platform banners use{' '}
        <code>notifications.announcement</code> and <code>notifications.maintenance</code> with{' '}
        <code>{'{ "active": true, "title": "...", "message": "..." }'}</code>.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={betaEnabled}
              disabled={betaSaving || saving}
              onChange={(e) => void handleBetaToggle(e.target.checked)}
            />
          }
          label="Beta mode"
        />
        <Typography variant="body2" color="text.secondary">
          While beta is on, visitors only see the free plan. Turn this off to show paid tiers and
          enable account upgrades.
        </Typography>
      </Paper>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load settings. Admin role with <strong>system:config</strong> permission required.
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Category</InputLabel>
          <Select
            label="Category"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
          }
          label="Show inactive"
        />
        <Box flex={1} />
        <Button variant="outlined" onClick={() => openCreate()}>
          Add setting
        </Button>
        {PLATFORM_PRESETS.map((preset) => (
          <Button key={preset.key} size="small" onClick={() => openCreate(preset)}>
            {preset.key.split('.').pop()}
          </Button>
        ))}
      </Stack>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : grouped.length === 0 ? (
        <Alert severity="info">No configuration entries yet. Add a preset or custom key.</Alert>
      ) : (
        grouped.map(([category, rows]) => (
          <Box key={category} sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ textTransform: 'capitalize' }}>
              {category}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Key</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {row.key}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {row.description ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.isActive ? 'active' : 'inactive'}
                          size="small"
                          color={row.isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => openEdit(row)}>
                          Edit
                        </Button>
                        {row.isActive && (
                          <Button
                            size="small"
                            color="warning"
                            disabled={deactivating}
                            onClick={() => setDeactivateTarget(row.key)}
                          >
                            Deactivate
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))
      )}

      <Dialog open={!!deactivateTarget} onClose={() => setDeactivateTarget(null)}>
        <DialogTitle>Deactivate configuration?</DialogTitle>
        <DialogContent>
          <Typography>
            <code>{deactivateTarget}</code> will stop applying until you re-enable it from the editor.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateTarget(null)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={confirmDeactivate} disabled={deactivating}>
            {deactivating ? 'Deactivating…' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editEntry ? `Edit ${editEntry.key}` : 'New configuration'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Key"
              value={editKey}
              onChange={(e) => setEditKey(e.target.value)}
              disabled={!!editEntry}
              fullWidth
              helperText="Unique identifier, e.g. notifications.announcement"
            />
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={editCategory}
                onChange={(e) => setEditCategory(e.target.value)}
              >
                {CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              fullWidth
            />
            <FormControlLabel
              control={<Switch checked={editActive} onChange={(e) => setEditActive(e.target.checked)} />}
              label="Active"
            />
            <TextField
              label="Value (JSON)"
              value={editValueJson}
              onChange={(e) => setEditValueJson(e.target.value)}
              multiline
              minRows={8}
              fullWidth
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !editKey.trim()}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </AdminPageGate>
  )
}

export default SystemSettings
